import React, { forwardRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  Stack,
  useTheme,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useFieldArray, Controller, useFormContext } from 'react-hook-form';
import { observer } from 'mobx-react';
import { DatePicker, FieldWrapperVerticalV2 } from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  IconLibrary,
  useAvailabilitySlotTypes,
} from '@product-framework/index';
import { FormattedMessage } from 'react-intl';

const DispatchCreateServiceRequests = forwardRef((props, ref) => {
  const { slotTypes } = useAvailabilitySlotTypes();
  const theme = useTheme();

  const emptyField = {
    requestDate: null,
    slotTypeCode: slotTypes[0].code,
  };

  const {
    control,
    formState: { errors },
    clearErrors,
  } = useFormContext();

  const maxFields = props?.field?.max || 3;

  const { fields, append, remove } = useFieldArray({
    control,
    name: props.name,
  });

  return (
    <Box
      ref={ref}
      sx={{
        mt: 2,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.palette.divider,
        borderRadius: 1,
        p: 1,
      }}>
      {fields.map((field, idx) => (
        <Grid2 container spacing={1} key={field.id} sx={{ mb: 1 }}>
          <Grid2 xs={4}>
            <Controller
              control={control}
              name={`${props?.name}.${idx}.requestDate`}
              render={({ field: controllerField }) => {
                const fieldError = {
                  [controllerField?.name]:
                    errors[props?.name]?.[idx]?.requestDate,
                };
                return (
                  <FieldWrapperVerticalV2
                    key={controllerField?.name}
                    field={{
                      name: controllerField?.name,
                      label: {
                        localeId: 'REQUESTED_DATE',
                        defaultMessage: 'Requested Date',
                      },
                      required: true,
                    }}
                    errors={fieldError}>
                    <DatePicker
                      {...controllerField}
                      disablePast
                      required
                      value={controllerField.value}
                      onChange={date => {
                        controllerField.onChange(date);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          InputLabelProps: {
                            shrink: true,
                            sx: { m: '0 !important' },
                          },
                        },
                      }}
                    />
                  </FieldWrapperVerticalV2>
                );
              }}
            />
          </Grid2>
          <Grid2 xs={7}>
            <Controller
              control={control}
              name={`${props?.name}.${idx}.slotTypeCode`}
              render={({ field: controllerField, fieldState: { error } }) => {
                const fieldError = {
                  [controllerField?.name]:
                    errors[props?.name]?.[idx]?.slotTypeCode,
                };

                return (
                  <FieldWrapperVerticalV2
                    key={controllerField?.name}
                    field={{
                      name: controllerField?.name,
                      label: {
                        localeId: 'SLOTS',
                        defaultMessage: 'Slots',
                      },
                      required: false,
                    }}
                    errors={fieldError}>
                    <Box
                      sx={{
                        display: 'flex',
                        ...(error && {
                          border: '1px solid',
                          pl: 1,
                          borderColor: 'error.main',
                          borderRadius: '8px',
                        }),
                      }}>
                      {slotTypes.map(item => {
                        return (
                          <Stack
                            key={item.code}
                            direction="row"
                            alignItems="center"
                            spacing={0.1}
                            component="label">
                            <FormControl error>
                              <FormControlLabel
                                control={
                                  <Radio
                                    checked={
                                      controllerField.value === item.code
                                    }
                                    onChange={() => {
                                      controllerField.onChange(item.code);
                                    }}
                                  />
                                }
                                label={item.name}
                              />
                            </FormControl>
                          </Stack>
                        );
                      })}
                    </Box>
                    <Box sx={{ minHeight: '10px' }}>
                      {error?.message && (
                        <FormHelperText error>{error.message}</FormHelperText>
                      )}
                    </Box>
                  </FieldWrapperVerticalV2>
                );
              }}
            />
          </Grid2>
          <Grid2 xs={1} mt={3}>
            <Button
              size="small"
              onClick={() => {
                remove(idx);
                clearErrors();
              }}
              color="error">
              <Delete />
            </Button>
          </Grid2>
        </Grid2>
      ))}
      {fields.length < maxFields && (
        <Button
          variant="text"
          startIcon={<IconLibrary.Add fontSize="inherit" />}
          onClick={() => {
            append({ ...emptyField });
          }}
          sx={{ mt: 0 }}>
          <FormattedMessage
            id="ADD_NEW_DATE_AND_SLOT"
            defaultMessage="Add New Date and Slot"
          />
        </Button>
      )}
      {errors?.[props?.name]?.type !== 'server' &&
        errors?.[props?.name]?.message && (
          <Box>
            <FormHelperText error>
              {errors?.[props?.name]?.message}
            </FormHelperText>
          </Box>
        )}
    </Box>
  );
});

export default observer(DispatchCreateServiceRequests);
